import { gql, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import {
  NewStoreItemConnectionFragmentDoc,
  NewStoreItemsQuery,
  NewStoreItemsQueryVariables,
  PrepaidPointsPaymentSourceFragmentDoc,
  PurchasablePrepaidPointConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { usePointPurchase } from '@/components/pages/PrepaidPointCharge/viewModel'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getPlatform } from '@/core/native/app'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId
  const platform = getPlatform()
  const {
    purchaseByIosAndAndroid,
    purchaseByWeb,
    completeNativePurchase,
    setCompleteNativePurchase,
    chargeLoading,
  } = usePointPurchase(user?.uid, platform, 'newStoreItems')

  const { data, loading, fetchMore } = useQuery<
    NewStoreItemsQuery,
    NewStoreItemsQueryVariables
  >(QUERY, {
    variables: {
      platform,
      artistId,
      filters: {
        isPublished: true,
        reviewStatus: 'accepted',
        salesStatus: 'active',
      },
    },
    skip: !user,
    notifyOnNetworkStatusChange: true,
    pollInterval: completeNativePurchase ? 2000 : 0,
  })

  useEffect(() => {
    setCompleteNativePurchase(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prepaidPointBalance])

  return {
    state: {
      items: data?.items ?? null,
      formattedPoint:
        data?.prepaidPointBalance.formattedPrepaidPointBarance ?? '0',
      purchasablePrepaidPoints: data?.purchasablePrepaidPoints ?? null,
      isCardRegistered: !!data?.paymentSource?.last4,
      loading,
      artistId,
      hasNextPage: !!data?.items?.pageInfo.hasNextPage,
      isLoggedIn: !!user?.uid,
      point: data?.prepaidPointBalance.balance ?? 0,
      pointChargeLoading: chargeLoading,
    },
    actions: {
      onPurchaseCharge:
        platform === 'web' ? purchaseByWeb : purchaseByIosAndAndroid,
      async onFetchMore() {
        await fetchMore({
          variables: {
            artistId,
            filters: {
              isPublished: true,
              reviewStatus: 'accepted',
              salesStatus: 'active',
            },
            pageInput: { cursor: data?.items?.pageInfo.endCursor },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query NewStoreItems(
    $artistId: ID!
    $filters: ItemsFilterInput
    $pageInput: PageInputDate
    $platform: String!
    $purchasablePrepaidPointPageInput: PageInput
  ) {
    items(artistId: $artistId, filters: $filters, pageInput: $pageInput) {
      ...NewStoreItemConnection
    }
    prepaidPointBalance {
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
    purchasablePrepaidPoints(
      platform: $platform
      pageInput: $purchasablePrepaidPointPageInput
    ) {
      ...PurchasablePrepaidPointConnection
    }
    paymentSource {
      ...PrepaidPointsPaymentSource
    }
  }
  ${NewStoreItemConnectionFragmentDoc}
  ${PrepaidPointsPaymentSourceFragmentDoc}
  ${PurchasablePrepaidPointConnectionFragmentDoc}
`
